import { useState } from "react";

import {
  useAccessCodes,
  useMintAccessCode,
  useRevokeAccessCode,
  type AccessCodeRow,
  type MintAccessCodeResponse,
} from "@/api/access-codes";
import { ApiError } from "@/api/client";
import { ErrorBanner } from "@/components/ErrorBanner";
import { RelativeTime } from "@/components/RelativeTime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * `/access-codes` — the M2 admin surface for minting + revoking
 * single-use access codes for the public chatbot soft launch.
 *
 * Two stacked sections: a mint form on top, a list of recently-minted
 * codes below. The list refreshes via TanStack invalidation whenever
 * a mint or revoke succeeds.
 *
 * **The plaintext code is never returned to this page.** The mint
 * response carries `{ id, expiresAt }` only; the 6-digit plaintext
 * is sent to the prospect's email by Resend. The UI surfaces this
 * loudly so the operator doesn't expect to see the code after submit.
 *
 * Security notes for the operator: codes expire 48 h after mint and
 * burn after 5 matched-but-rejected redemption attempts. The
 * burn-threshold is **forensic-only, not defensive** — codes are
 * stored hashed, so an unmatched plaintext can't be attributed to
 * any specific row. The defensive brute-force cap lives in the
 * per-IP rate limiter described in `docs/public-auth.md`.
 */
export function AccessCodes() {
  const list = useAccessCodes();

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-light tracking-tight">Access codes</h1>
        <p className="mt-2 text-sm text-text-muted max-w-2xl">
          Mint a single-use 6-digit code for a named prospect. The code is
          emailed to their address and expires 48 hours after mint. Codes
          authorise one chatbot session; the prospect&apos;s session lasts
          7 days from redemption.
        </p>
      </header>

      <MintForm />

      <section className="space-y-3">
        <h2 className="text-xs font-mono uppercase tracking-label text-text-muted">
          Recent codes
        </h2>
        {list.isLoading ? (
          <p className="text-sm text-text-muted">Loading…</p>
        ) : list.isError ? (
          <ErrorBanner error={list.error} onRetry={() => void list.refetch()} />
        ) : list.data && list.data.codes.length > 0 ? (
          <CodesTable rows={list.data.codes} />
        ) : (
          <p className="text-sm text-text-muted">No codes minted yet.</p>
        )}
      </section>
    </div>
  );
}

interface MintFormState {
  recipientName: string;
  recipientCompany: string;
  recipientEmail: string;
}

const EMPTY_FORM: MintFormState = {
  recipientName: "",
  recipientCompany: "",
  recipientEmail: "",
};

function MintForm() {
  const mint = useMintAccessCode();
  const [form, setForm] = useState<MintFormState>(EMPTY_FORM);
  const [lastMint, setLastMint] = useState<MintAccessCodeResponse | null>(null);

  const canSubmit =
    form.recipientName.trim().length > 0 &&
    form.recipientCompany.trim().length > 0 &&
    /.+@.+/.test(form.recipientEmail.trim()) &&
    !mint.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    mint.mutate(
      {
        recipientName: form.recipientName.trim(),
        recipientCompany: form.recipientCompany.trim(),
        recipientEmail: form.recipientEmail.trim(),
      },
      {
        onSuccess: (data) => {
          setLastMint(data);
          setForm(EMPTY_FORM);
        },
      },
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-xs font-mono uppercase tracking-label text-text-muted mb-4">
        Mint a new code
      </h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field
          label="Prospect name"
          value={form.recipientName}
          onChange={(v) => setForm((s) => ({ ...s, recipientName: v }))}
          placeholder="Jane Smith"
          autoComplete="off"
        />
        <Field
          label="Company"
          value={form.recipientCompany}
          onChange={(v) => setForm((s) => ({ ...s, recipientCompany: v }))}
          placeholder="Acme Inc."
          autoComplete="off"
        />
        <Field
          label="Email"
          value={form.recipientEmail}
          onChange={(v) => setForm((s) => ({ ...s, recipientEmail: v }))}
          placeholder="jane@acme.example"
          type="email"
          autoComplete="off"
        />
        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={!canSubmit}>
            {mint.isPending ? "Minting…" : "Mint + send email"}
          </Button>
          {mint.isError && (
            <span className="text-sm text-red-500">
              {describeApiError(mint.error)}
            </span>
          )}
        </div>
      </form>

      {lastMint && (
        <div className="mt-4 p-3 rounded border border-accent/40 bg-accent/5 text-sm">
          <p className="text-text">
            Code minted ({lastMint.id.slice(0, 8)}…). The 6-digit code was
            emailed to the recipient — it is not shown here.
          </p>
          {lastMint.error === "email_send_failed" && (
            <p className="mt-2 text-red-500">
              Warning: the row was created but the Resend email failed.
              Revoke the code below and try again, or share the code
              directly via another channel.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function CodesTable({ rows }: { rows: AccessCodeRow[] }) {
  const revoke = useRevokeAccessCode();
  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Recipient</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead aria-label="actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const status = statusOf(row);
            return (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.recipientName}</TableCell>
                <TableCell>{row.recipientCompany}</TableCell>
                <TableCell className="font-mono text-xs">{row.recipientEmail}</TableCell>
                <TableCell>
                  <StatusBadge status={status} attemptCount={row.attemptCount} />
                </TableCell>
                <TableCell className="text-xs text-text-muted">
                  <RelativeTime date={row.createdAt} />
                </TableCell>
                <TableCell className="text-xs text-text-muted">
                  <RelativeTime date={row.expiresAt} />
                </TableCell>
                <TableCell className="text-right">
                  {status === "active" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(row.id)}
                    >
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

type CodeStatus = "active" | "used" | "revoked" | "expired" | "burned";

function statusOf(row: AccessCodeRow): CodeStatus {
  if (row.burnedAt) return "burned";
  if (row.usedAt) return "used";
  if (row.revokedAt) return "revoked";
  if (new Date(row.expiresAt).getTime() <= Date.now()) return "expired";
  return "active";
}

function StatusBadge({
  status,
  attemptCount,
}: {
  status: CodeStatus;
  attemptCount: number;
}) {
  const { variant, label } = statusDisplay(status);
  return (
    <span className="inline-flex items-center gap-2">
      <Badge variant={variant}>{label}</Badge>
      {attemptCount > 0 && (
        <span className="text-xs text-text-muted" title="Matched-but-rejected attempts">
          {attemptCount} attempt{attemptCount === 1 ? "" : "s"}
        </span>
      )}
    </span>
  );
}

function statusDisplay(status: CodeStatus): {
  variant: "accent" | "success" | "danger" | "muted";
  label: string;
} {
  switch (status) {
    case "active":
      return { variant: "accent", label: "Active" };
    case "used":
      return { variant: "success", label: "Used" };
    case "revoked":
      return { variant: "muted", label: "Revoked" };
    case "expired":
      return { variant: "muted", label: "Expired" };
    case "burned":
      return { variant: "danger", label: "Burned" };
  }
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  type,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoComplete?: string;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-label text-text-muted">
        {label}
      </span>
      <input
        type={type ?? "text"}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded border border-border bg-bg-card px-3 py-2 text-sm text-text placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      />
    </label>
  );
}

function describeApiError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return "Check the form — one of the fields is invalid.";
    if (err.status === 401) return "Your session expired; sign in again.";
    if (err.status === 502 || err.status === 503) {
      return "Server-side problem. The code row may have been created but the email send failed.";
    }
    return `Server returned ${err.status}.`;
  }
  return "Unexpected error.";
}
